<script setup lang="ts">
import CoreChipAction from 'frontcore/views/chips/chip/views/CoreChipAction.vue'
import i18n from 'frontcore/i18n'
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import useCalendarHeader from 'frontcore/presentation/calendar/domain/useCalendarHeader'
import { CalendarModeOptions } from 'frontcore/presentation/calendar/domain/types'
import { toRefs, watch } from 'vue'

interface Props {
  mode: number
  month: number
  selectionMode: number
  year: number
  disablePastDays?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disablePastDays: false,
})

const { selectionMode } = toRefs(props)

const numberOfVisibleYears = 9

const emit = defineEmits<{
  (e: 'update:month', value: number): void,
  (e: 'update:year', value: number): void,
  (e: 'update:mode', value: CalendarModeOptions): void,
  (e: 'select:year', value: number): void,
  (e: 'today'): void,
}>()

const {
  viewmodel,
  goToYear,
  goToToday,
  nextYearList,
  previousYearList,
  setCalendarHeaderInfo,
  showTodayButton,
} = useCalendarHeader(
  props.mode,
  props.month,
  props.year,
  numberOfVisibleYears,
  selectionMode
)

watch(
  () => props.mode,
  () => {
    setCalendarHeaderInfo(props.month, props.year)
  }
)

const previous = () => {
  previousYearList()
  emit('update:year', viewmodel.year)
  emit('update:month', viewmodel.month)
}

const next = () => {
  nextYearList()
  emit('update:year', viewmodel.year)
  emit('update:month', viewmodel.month)
}

const moveToToday = () => {
  goToToday()
  emit('update:year', viewmodel.year)
  emit('update:month', viewmodel.month)
  emit('update:mode', viewmodel.mode)
  emit('today')
}

const selectYear = (yearValue: number) => {
  if (isDisabled(yearValue)) { return }
  goToYear(yearValue)
  emit('update:year', viewmodel.year)
  emit('update:mode', viewmodel.mode)
  emit('select:year', yearValue)
}
const { t } = i18n.global

const isSelected = (yearValue: number) => {
  return props.year === yearValue
}

const isDisabled = (yearValue: number) => {
  return props.disablePastDays && yearValue < new Date().getFullYear()
}
</script>

<template>
  <div class="calendar__menu__year-selector flex flex-col">
    <div
      data-test="calendar-header"
      class="flex flex-none items-center gap-6">
      <CoreChipAction
        v-show="showTodayButton"
        :label="t('common.messages.today')"
        class="core-calendar__menu__header-action"
        dataTest="calendar-today-chip"
        @click="moveToToday" />
      <div class="calendar-header flex items-start justify-between user-select-none">
        <SVGIcon
          class="pointer"
          iconName="ChevronLeft"
          data-test="calendar-header-previous-years-icon"
          @click="previous" />
        <span
          class="headlines--headline-04 capitalize-text flex justify-content-center align-content-start"
          data-test="calendar-header-years-list">
          {{ viewmodel.yearList[0] }} - {{ viewmodel.yearList[numberOfVisibleYears-1] }}
        </span>
        <SVGIcon
          class="pointer"
          iconName="ChevronRight"
          data-test="calendar-header-next-years-icon"
          @click="next" />
      </div>
    </div>
    <div class="calendar__menu__years flex-auto grid user-select-none">
      <div
        v-for="yearValue in viewmodel.yearList"
        :key="`calendar-select-year-${yearValue}`"
        :class="[{
          'bg-core-primary-80': isSelected(yearValue),
          'calendar__menu__year__item--disabled': isDisabled(yearValue)
        }]"
        class="calendar__menu__year__item flex items-center justify-center"
        @click="selectYear(yearValue)">
        <span
          class="buttons--button-01 uppercase-text text-core-primary-10"
          :class="{
            'calendar__menu__year__name--disabled': isDisabled(yearValue)
          }"
          :data-test="`calendar-select-year-${yearValue}`">
          {{ yearValue }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.calendar__menu__year-selector {
  gap: var(--cell-gap);
  height: 17rem;
}

.calendar__menu__years {
  gap: var(--cell-gap);
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
}

.calendar__menu__year__item {
  border-radius: var(--s-1);
}

.calendar__menu__year__item:hover {
  background-color: var(--core-primary-90);
}

.calendar__menu__year__item:active {
  background-color: var(--core-primary-80);
}

.calendar__menu__year__item--disabled:hover {
  background-color: var(--core-neutral-cold-80);
}

.calendar__menu__year__name--disabled {
  color: var(--core-neutral-cold-60);
}
</style>
